// OfferAssociatedCollabs.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  Modal,
  TouchableOpacity,
  Image,
  FlatList,
  TextInput,
  Linking,
  ActivityIndicator,
  useWindowDimensions,
  Alert,
} from 'react-native';
import { Calendar } from 'react-native-calendars';
import { Ionicons } from '@expo/vector-icons';
import QRCode from 'react-native-qrcode-svg';
import { LinearGradient } from 'expo-linear-gradient';

const OfferAssociatedCollabs = ({ route, navigation }) => {
  const { ListingID } = route.params;

  const [collabs, setCollabs] = useState([]);
  const [filteredCollabs, setFilteredCollabs] = useState([]);
  const [listingViews, setListingViews] = useState({});
  const [listingVisits, setListingVisits] = useState({});
  const [searchText, setSearchText] = useState('');
  const [toggleStatus, setToggleStatus] = useState(''); // current Pause/Live status

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newToggleStatus, setNewToggleStatus] = useState(toggleStatus);
  const [loading, setLoading] = useState(true);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState({});

  const [combinedFollowers, setCombinedFollowers] = useState(null);
  const [cfLoading, setCfLoading] = useState(true);

  // ✅ NEW: subscription / auth state for sidenav lock + CTA
  const [isAuthed, setIsAuthed] = useState(false);
  const [authMode, setAuthMode] = useState('none'); // "subscriber" | "none"
  const [authChecked, setAuthChecked] = useState(false);
  const isSubscriber = authMode === 'subscriber';

  // UI controls
  const [activeStatuses, setActiveStatuses] = useState(new Set()); // 'Pending','Upcoming','Redeemed','Expired','Cancelled'
  const [sortBy, setSortBy] = useState('Newest'); // 'Newest' | 'Oldest' | 'A→Z' | 'Z→A' | 'Status'
  const STATUS_ORDER = { Pending: 1, Upcoming: 2, Redeemed: 3, Expired: 4, Cancelled: 5 };

  const [isPauseBtnPressed, setIsPauseBtnPressed] = useState(false);
  const [quickFilterActive, setQuickFilterActive] = useState(false);

  // Calendar selection
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const { width } = useWindowDimensions();
  const isCompact = width < 800; // treat phones / small tablet as compact
  const titleBaseSize = width < 340 ? 20 : width < 390 ? 22 : 24;

  // ✅ NEW: open link in same tab (web) or via Linking (native)
  const openInSameTab = (url) => {
    if (typeof window !== 'undefined') {
      window.location.href = url;
    } else {
      Linking.openURL(url);
    }
  };

  // ✅ NEW: open offer preview (new tab on web, normal Linking on native)
const openOfferPreview = () => {
  const url = `https://collabbay.com/collaboffer/${ListingID}`;

  // Web: open a new tab (so you can easily come back)
  if (typeof window !== 'undefined' && window?.open) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  // Native fallback
  Linking.openURL(url);
};


  // --- Status helper (logic preserved) ---
  function getCollabStatus(collab) {
    if (collab?.Status === 'Cancelled') return 'Cancelled';

    const today = new Date();
    const creditAvailableDate = new Date(collab?.CreditAvailableStart);

    if (creditAvailableDate < today) {
      if (collab?.Redeemed) return 'Redeemed';
      return 'Expired';
    } else {
      if (collab?.Status === 'Accepted') return 'Upcoming';
      if (collab?.Status === 'Pending') return 'Pending';
    }
    return null;
  }

  // Helper to make "YYYY-MM-DD" keys from dates
  const getDateKeyFromString = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // format visit date (for better visual prominence)
  const formatVisitDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString; // fallback

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';

    return `${day}${suffix} ${month} ${year}`;
  };

  // Pretty label for calendar summary like "5th Dec 2025"
  const formatSummaryDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return isoString;

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();

    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';

    return `${day}${suffix} ${month} ${year}`;
  };

  // "X days" helper
  const getDaysDiffLabel = (dateString) => {
    if (!dateString) return null;
    const visit = new Date(dateString);
    if (Number.isNaN(visit.getTime())) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    visit.setHours(0, 0, 0, 0);

    const diffMs = visit.getTime() - today.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 1) return `In ${diffDays} days`;
    if (diffDays === 1) return 'In 1 day';
    if (diffDays === 0) return 'Today';
    if (diffDays === -1) return '1 day ago';
    return `${Math.abs(diffDays)} days ago`;
  };

  // Keep quickFilterActive in sync with status chips
  useEffect(() => {
    const hasPending = activeStatuses.has('Pending');
    const hasUpcoming = activeStatuses.has('Upcoming');
    if (activeStatuses.size === 2 && hasPending && hasUpcoming) {
      if (!quickFilterActive) setQuickFilterActive(true);
    } else if (quickFilterActive) {
      setQuickFilterActive(false);
    }
  }, [activeStatuses, quickFilterActive]);

  // Final list shown (search already applied to filteredCollabs)
  const displayedCollabs = useMemo(() => {
    let arr = [...filteredCollabs];

    // Filter by status chips (if any selected)
    if (activeStatuses.size > 0) {
      arr = arr.filter((c) => {
        const s = getCollabStatus(c);
        return s && activeStatuses.has(s);
      });
    }

    // Sort
    arr.sort((a, b) => {
      if (sortBy === 'Newest' || sortBy === 'Oldest') {
        const da = new Date(a.Date).getTime();
        const db = new Date(b.Date).getTime();
        return sortBy === 'Newest' ? db - da : da - db;
      }
      if (sortBy === 'A→Z' || sortBy === 'Z→A') {
        const ca = (a.Promoter || '').toLowerCase();
        const cb = (b.Promoter || '').toLowerCase();
        return sortBy === 'A→Z' ? ca.localeCompare(cb) : cb.localeCompare(ca);
      }
      if (sortBy === 'Status') {
        const sa = STATUS_ORDER[getCollabStatus(a) || 'zz'] || 999;
        const sb = STATUS_ORDER[getCollabStatus(b) || 'zz'] || 999;
        return sa - sb;
      }
      return 0;
    });

    return arr;
  }, [filteredCollabs, activeStatuses, sortBy]);

  // Acceptance stats (for metrics row)
  const { totalRequests, acceptedCount, acceptanceRate } = useMemo(() => {
    const total = collabs.length;
    const accepted = collabs.filter((c) => c.Status === 'Accepted').length;
    const rate = total > 0 ? `${Math.round((accepted / total) * 100)}%` : '--';
    return {
      totalRequests: total,
      acceptedCount: accepted,
      acceptanceRate: rate,
    };
  }, [collabs]);

  // --- Calendar markings: Pending & Upcoming only ---
  const calendarMarks = useMemo(() => {
    const marks = {};
    const pendingColor = '#facc15'; // bright yellow
    const upcomingColor = '#22c55e'; // bright green

    displayedCollabs.forEach((c) => {
      const status = getCollabStatus(c);
      if (status !== 'Pending' && status !== 'Upcoming') return;

      const key = getDateKeyFromString(c.CreditAvailableStart);
      if (!key) return;

      if (!marks[key]) {
        marks[key] = { dots: [], marked: true };
      }

      const dots = marks[key].dots || [];
      const hasPendingDot = dots.some((d) => d.key === 'Pending');
      const hasUpcomingDot = dots.some((d) => d.key === 'Upcoming');

      if (status === 'Pending' && !hasPendingDot) {
        dots.push({ key: 'Pending', color: pendingColor, selectedDotColor: pendingColor });
      }
      if (status === 'Upcoming' && !hasUpcomingDot) {
        dots.push({ key: 'Upcoming', color: upcomingColor, selectedDotColor: upcomingColor });
      }

      marks[key].dots = dots;
    });

    if (selectedCalendarDate && marks[selectedCalendarDate]) {
      marks[selectedCalendarDate] = {
        ...marks[selectedCalendarDate],
        selected: true,
        selectedColor: '#111827',
        selectedTextColor: '#fdf05d',
      };
    }

    return marks;
  }, [displayedCollabs, selectedCalendarDate, getCollabStatus]);

  // Collabs for the currently selected calendar day (Pending + Upcoming only)
  const selectedDateCollabs = useMemo(() => {
    if (!selectedCalendarDate) return [];
    return displayedCollabs.filter((c) => {
      const status = getCollabStatus(c);
      if (status !== 'Pending' && status !== 'Upcoming') return false;
      const key = getDateKeyFromString(c.CreditAvailableStart);
      return key === selectedCalendarDate;
    });
  }, [displayedCollabs, selectedCalendarDate]);

  // "Upcoming soon" — next 3 days, Pending / Upcoming only
  const upcomingSoonCollabs = useMemo(() => {
    if (!displayedCollabs || displayedCollabs.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setDate(limit.getDate() + 3);

    return displayedCollabs
      .filter((c) => {
        const status = getCollabStatus(c);
        if (status !== 'Pending' && status !== 'Upcoming') return false;
        const d = new Date(c.CreditAvailableStart);
        if (Number.isNaN(d.getTime())) return false;
        d.setHours(0, 0, 0, 0);
        return d >= today && d <= limit;
      })
      .sort(
        (a, b) =>
          new Date(a.CreditAvailableStart).getTime() -
          new Date(b.CreditAvailableStart).getTime()
      )
      .slice(0, 5);
  }, [displayedCollabs]);

  const toggleStatusChip = (status) => {
    const next = new Set(activeStatuses);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    setActiveStatuses(next);
  };

  const clearStatusChips = () => setActiveStatuses(new Set());

  const handleQuickFilterPress = () => {
    if (quickFilterActive) {
      clearStatusChips();
    } else {
      setActiveStatuses(new Set(['Pending', 'Upcoming']));
    }
  };

  const fetchCombinedFollowers = () => {
    setCfLoading(true);
    fetch(
      `https://collabbayadmin.com/APIControllers/FetchCollabsCombinedFollowerCount.php?ListingID=${ListingID}`,
      {
        credentials: 'include',
      }
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success') {
          setCombinedFollowers({
            human: data.human_readable,
            raw: data.combined_followers,
          });
        } else {
          setCombinedFollowers({ human: '—', raw: 0 });
        }
      })
      .catch(() => setCombinedFollowers({ human: '—', raw: 0 }))
      .finally(() => setCfLoading(false));
  };

  const fetchUserData = () => {
    fetch('https://collabbayadmin.com/APIControllers/GetSessionData.php')
      .then((response) => response.json())
      .then((data) => setUserData(data))
      .catch((error) => {
        console.error('Error fetching user data: ', error);
      });
  };

  useEffect(() => {
    fetch(
      `https://collabbayadmin.com/APIControllers/RetrieveListingAssociatedCollabs.php?ListingID=${ListingID}`
    )
      .then((response) => response.json())
      .then((data) => {
        setCollabs(data);
        setFilteredCollabs(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching collabs:', error);
        setLoading(false);
      });

    fetchListingViews();
    fetchListingVisits();
    fetchCombinedFollowers();
  }, []);

  // ✅ unified auth for sidenav + page lock
  useEffect(() => {
    fetch('https://collabbayadmin.com/APIControllers/UserSubscriptionAuth.php', {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        const authed =
          typeof data.authenticated === 'boolean'
            ? data.authenticated
            : data.status === 'active';
        const mode = data.auth_mode
          ? data.auth_mode
          : data.status === 'active'
          ? 'subscriber'
          : 'none';

        setIsAuthed(!!authed);
        setAuthMode(mode);
        setIsAuthenticated(!!authed);
      })
      .catch((error) => {
        console.error('Auth check failed:', error);
      })
      .finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    fetchListingStatus();
  }, []);

  useEffect(() => {
    fetchUserData();
  }, []);

  const openListing = () => {
    const url = `https://collabbay.com/collaboffer/${ListingID}`;
    Alert.alert(
      'Open offer preview',
      'This will open the offer in a new tab so you can preview it as a creator. To come back here, just close that tab.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Open preview',
          onPress: () => Linking.openURL(url),
        },
      ]
    );
  };

  const handleDownloadContent = () => {
    const url = `https://collabbayadmin.com/DownloadListingContent.php?ListingID=${ListingID}`;
    Linking.openURL(url);
  };

  const fetchListingStatus = () => {
    fetch(
      `https://collabbayadmin.com/APIControllers/GetCurrentPauseLiveStatus.php?ListingID=${ListingID}`
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          setToggleStatus(data.PauseLiveToggle);
          setNewToggleStatus(data.PauseLiveToggle);
        } else {
          console.error('Error fetching listing status:', data.message);
        }
      })
      .catch((error) => console.error('Error fetching listing status:', error));
  };

  const handleSubmitStatusChange = () => {
    const url = `https://collabbayadmin.com/APIControllers/PauseActivateListing.php`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ListingID: ListingID,
        PauseLiveStatusValue: newToggleStatus,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 'success') {
          setToggleStatus(newToggleStatus);
          setIsModalVisible(false);
          // expo-web usage is fine here
          // eslint-disable-next-line no-undef
          window.location.reload();
        } else {
          console.error('Failed to update status:', data.message);
        }
      })
      .catch((error) => console.error('Error updating status:', error));
  };

  const getStatusWithDot = (status) => {
    let color = 'grey';

    if (status === 'Live') {
      color = '#16a34a';
    } else if (status === 'Pause') {
      color = '#dc2626';
    }

    return (
      <View
        style={{
          flexDirection: 'row',
          marginTop: 8,
          alignItems: 'center',
        }}
      >
        <Text style={{ fontSize: 12, color: '#4b5563' }}>Current Status · {status}</Text>
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: color,
            marginLeft: 6,
          }}
        />
      </View>
    );
  };

  useEffect(() => {
    // You still fetch views/visits for potential future use, but no longer show them
  }, [listingViews.totalViews, listingVisits.totalCount]);

  useEffect(() => {
    if (searchText) {
      setFilteredCollabs(
        collabs.filter(
          (collab) =>
            collab.Promoter.toLowerCase().includes(searchText.toLowerCase()) ||
            collab.AdHeader.toLowerCase().includes(searchText.toLowerCase())
        )
      );
    } else {
      setFilteredCollabs(collabs);
    }
  }, [searchText, collabs]);

  const fetchListingViews = () => {
    fetch(
      `https://collabbayadmin.com/APIControllers/RetrieveListingViews.php?ListingID=${ListingID}`
    )
      .then((response) => response.json())
      .then((data) => setListingViews(data))
      .catch((error) => console.error('Error fetching listing views:', error));
  };

  const fetchListingVisits = () => {
    fetch(
      `https://collabbayadmin.com/APIControllers/RetrieveListingConversions.php?ListingID=${ListingID}`
    )
      .then((response) => response.json())
      .then((data) => setListingVisits(data))
      .catch((error) => console.error('Error fetching listing visits:', error));
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Expired':
        return styles.expiredStatus;
      case 'Redeemed':
        return styles.redeemedStatus;
      case 'Upcoming':
        return styles.upcomingStatus;
      case 'Pending':
        return styles.pendingStatus;
      case 'Cancelled':
        return styles.cancelledStatus;
      default:
        return null;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Expired':
        return <Ionicons name="time-outline" size={16} color="#991b1b" />;
      case 'Redeemed':
        return <Ionicons name="checkmark-circle-outline" size={16} color="#166534" />;
      case 'Upcoming':
        return <Ionicons name="calendar-outline" size={16} color="#075985" />;
      case 'Pending':
        return <Ionicons name="alert-circle-outline" size={16} color="#92400e" />;
      default:
        return null;
    }
  };

  const renderCollabItem = ({ item }) => {
    const collabStatus = getCollabStatus(item);
    const visitDateFormatted = formatVisitDate(item.CreditAvailableStart);
    const daysLabel = getDaysDiffLabel(item.CreditAvailableStart);

    return (
      <TouchableOpacity
        style={styles.collabsCard}
        onPress={() => {
          if (!isAuthenticated) {
            Linking.openURL('https://collabbayadmin.com/subscribe');
          } else {
            navigation.navigate('BookingRequest', { CollabID: item.CollabID });
          }
        }}
      >
        {/* top row: avatar + name + status + chevron */}
        <View style={styles.collabCardHeaderRow}>
          <View style={styles.collabHeaderLeft}>
            <View style={styles.collabCreatorRow}>
              <View style={styles.creatorAvatar}>
                {item.ProfileImage ? (
                  <Image
                    source={{ uri: item.ProfileImage }}
                    style={styles.creatorAvatarImage}
                  />
                ) : (
                  <Text style={styles.creatorAvatarText}>
                    {item.Promoter?.charAt(0)?.toUpperCase() || '?'}
                  </Text>
                )}
              </View>

              <View style={{ flexShrink: 1 }}>
                <Text style={styles.collabsCardIDTextPromoter}>
                  {!isAuthenticated ? (
                    <>
                      {item.Promoter.charAt(0)}
                      <Text
                        style={{
                          color: 'transparent',
                          textShadowColor: 'rgba(0, 0, 0, 0.7)',
                          textShadowOffset: { width: 0, height: 0 },
                          textShadowRadius: 10,
                          userSelect: 'none',
                          letterSpacing: 2,
                        }}
                        selectable={false}
                      >
                        {item.Promoter.slice(1)}
                      </Text>
                    </>
                  ) : (
                    item.Promoter
                  )}
                </Text>
                <Text style={styles.collabDateText}>
                  Requested on{' '}
                  {(() => {
                    const date = new Date(item.Date);
                    const day = date.getDate();
                    const month = date.toLocaleString('default', { month: 'short' });
                    const suffix =
                      day % 10 === 1 && day !== 11
                        ? 'st'
                        : day % 10 === 2 && day !== 12
                        ? 'nd'
                        : day % 10 === 3 && day !== 13
                        ? 'rd'
                        : 'th';
                    return `${day}${suffix} ${month}`;
                  })()}
                </Text>
              </View>
            </View>
          </View>

          <View style={styles.collabHeaderRight}>
            {collabStatus && (
              <View style={[styles.statusBadge, getStatusStyle(collabStatus)]}>
                {getStatusIcon(collabStatus)}
                <Text style={styles.statusText}>{collabStatus}</Text>
              </View>
            )}
            <View style={styles.cardChevronWrapper}>
              <Ionicons name="chevron-forward" size={24} color="#9ca3af" />
            </View>
          </View>
        </View>

        {/* meta row: visit date + X days */}
        <View style={styles.collabMetaRow}>
          <View style={[styles.metaItem, styles.metaItemPrimary]}>
            <View style={styles.metaIconRow}>
              <Ionicons name="calendar-outline" size={16} color="#1d4ed8" />
              <View style={{ marginLeft: 6 }}>
                <Text style={styles.metaLabelPrimary}>Visit date</Text>
                <Text style={styles.metaValuePrimary}>{visitDateFormatted}</Text>
                {daysLabel && (
                  <Text style={styles.metaDaysText}>{daysLabel}</Text>
                )}
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // -------- LOADING STATE (WITH SIDENAV) --------
  if (loading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.appShell}>
          <View style={styles.shellRow}>
            {/* ✅ SIDENAV */}
            <View style={styles.sideNav}>
              <LinearGradient
                colors={['#050509', '#050505', '#000000']}
                style={styles.sideNavInner}
              >
                {/* subtle glow blob */}
                <LinearGradient
                  colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                  style={styles.sideNavGlow}
                  start={{ x: 0.1, y: 0 }}
                  end={{ x: 1, y: 1 }}
                />

                {/* TOP: Logo + wordmark */}
                <View style={styles.sideNavHeader}>
                  <Image
                    source={{
                      uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                    }}
                    style={styles.sideNavLogoImage}
                    resizeMode="contain"
                  />
                  <View>
                    <Text style={styles.logoWordmark}>
                      <Text style={{ fontWeight: '300' }}>collab</Text>
                      bay
                      <Text style={styles.logoDot}>.</Text>
                    </Text>
                    <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                  </View>
                </View>

                {/* MAIN NAV SECTION */}
                <View style={styles.sideNavSection}>
                  {/* Username pill */}
                  <LinearGradient
                    colors={['#111111', '#2b2b2b']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 0 }}
                    style={styles.usernamePill}
                  >
                    <Text
                      numberOfLines={1}
                      ellipsizeMode="tail"
                      style={styles.usernamePillText}
                    >
                      {userData.Username
                        ? ` ${userData.Username}`
                        : 'Welcome Back'}
                    </Text>
                  </LinearGradient>

                  {/* Home → MyPanel */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyPanel')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="home-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Home</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* My Offers → MyOffers */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('MyOffers')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="pricetags-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>My Offers</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) {
                        openInSameTab('https://collabbayadmin.com/subscribe');
                      } else {
                        navigation.navigate('MyCalendar');
                      }
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="calendar-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>My Calendar</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons
                          name="lock-closed-outline"
                          size={16}
                          color="#FDF05D"
                          style={styles.lockIcon}
                        />
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Browse Creators → BrowseCreators */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => navigation.navigate('BrowseCreators')}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="people-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Browse Creators</Text>
                      </View>
                    </View>
                  </TouchableOpacity>

                  {/* Activity → MyActivityFeed (LOCKED if NOT authed) */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() => {
                      if (authChecked && !isAuthed) {
                        openInSameTab('https://collabbayadmin.com/subscribe');
                      } else {
                        navigation.navigate('MyActivityFeed');
                      }
                    }}
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="chatbubbles-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Activity</Text>
                      </View>
                      {authChecked && !isAuthed && (
                        <Ionicons
                          name="lock-closed-outline"
                          size={16}
                          color="#FDF05D"
                          style={styles.lockIcon}
                        />
                      )}
                    </View>
                  </TouchableOpacity>

                  {/* Account → BusinessAccountPage */}
                  <TouchableOpacity
                    style={styles.navItem}
                    onPress={() =>
                      navigation.navigate('BusinessAccountPage')
                    }
                  >
                    <View style={styles.navItemRow}>
                      <View style={styles.navItemMainLabel}>
                        <Ionicons
                          name="person-circle-outline"
                          size={20}
                          color="#f5f5f5"
                          style={styles.navIcon}
                        />
                        <Text style={styles.navText}>Account</Text>
                      </View>
                    </View>
                  </TouchableOpacity>
                </View>

                {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
                <View style={styles.sideNavSection}>
                  {/* Peer Map */}
                  <TouchableOpacity
                    style={styles.peerMapCard}
                    activeOpacity={0.9}
                    onPress={() => openInSameTab('/venues')}
                  >
                    <LinearGradient
                      colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                      style={styles.peerMapInner}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.peerMapContent}>
                        <Ionicons
                          name="map-outline"
                          size={18}
                          color="#FDF05D"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.peerMapTitle}>Peer Map</Text>
                          <Text style={styles.peerMapSubtitle}>
                            View 100+ venues on Collabbay
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>

                  {/* Subscribe CTA — only if NOT subscriber */}
                  {!isSubscriber && (
                    <TouchableOpacity
                      style={styles.subscribeCard}
                      activeOpacity={0.9}
                      onPress={() =>
                        openInSameTab('https://collabbayadmin.com/subscribe')
                      }
                    >
                      <LinearGradient
                        colors={['#FDF05D', '#fbbf24']}
                        style={styles.subscribeGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 1 }}
                      >
                        <View style={styles.subscribeContent}>
                          <Ionicons
                            name="rocket-outline"
                            size={20}
                            color="#000"
                            style={{ marginRight: 8 }}
                          />
                          <View style={{ flex: 1 }}>
                            <Text style={styles.subscribeTitle}>
                              Subscribe & unlock
                            </Text>
                            <Text style={styles.subscribeSubtitle}>
                              Unlock Activity & Calendar, plus more.
                            </Text>
                          </View>
                        </View>
                      </LinearGradient>
                    </TouchableOpacity>
                  )}
                </View>

                {/* BOTTOM: FAQ + Help */}
                <View style={styles.sideNavBottom}>
                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/FAQs')
                    }
                  >
                    <Ionicons
                      name="help-circle-outline"
                      size={18}
                      color="#f5f5f5"
                      style={{ marginRight: 8 }}
                    />
                    <View>
                      <Text style={styles.bottomLinkText}>FAQs</Text>
                      <Text style={styles.bottomLinkCaption}>
                        Common questions answered
                      </Text>
                    </View>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.bottomLink}
                    onPress={() =>
                      Linking.openURL('mailto:info@collab-bay.com')
                    }
                  >
                    <Ionicons
                      name="headset-outline"
                      size={18}
                      color="#f5f5f5"
                      style={{ marginRight: 8 }}
                    />
                    <View>
                      <Text style={styles.bottomLinkText}>Help</Text>
                      <Text style={styles.bottomLinkCaption}>
                        We&apos;re here 24/7
                      </Text>
                    </View>
                  </TouchableOpacity>
                </View>
              </LinearGradient>
            </View>

            {/* MAIN AREA */}
            <View style={styles.mainArea}>
              <View style={styles.mainInner}>
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color="#000" />
                  <Text style={{ marginTop: 10, color: '#4b5563', fontSize: 13 }}>
                    Loading collab requests…
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // -------- MAIN RENDER (WITH SIDENAV) --------
  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.appShell}>
        <View style={styles.shellRow}>
          {/* ✅ SIDENAV */}
          <View style={styles.sideNav}>
            <LinearGradient
              colors={['#050509', '#050505', '#000000']}
              style={styles.sideNavInner}
            >
              {/* subtle glow blob */}
              <LinearGradient
                colors={['rgba(253,240,93,0.08)', 'rgba(0,0,0,0)']}
                style={styles.sideNavGlow}
                start={{ x: 0.1, y: 0 }}
                end={{ x: 1, y: 1 }}
              />

              {/* TOP: Logo + wordmark */}
              <View style={styles.sideNavHeader}>
                <Image
                  source={{
                    uri: 'https://collabbayadmin.com/CollabbayLogoDark.png',
                  }}
                  style={styles.sideNavLogoImage}
                  resizeMode="contain"
                />
                <View>
                  <Text style={styles.logoWordmark}>
                    <Text style={{ fontWeight: '300' }}>collab</Text>
                    bay
                    <Text style={styles.logoDot}>.</Text>
                  </Text>
                  <Text style={styles.logoDesktopTag}>DESKTOP</Text>
                </View>
              </View>

              {/* MAIN NAV SECTION */}
              <View style={styles.sideNavSection}>
                {/* Username pill */}
                <LinearGradient
                  colors={['#111111', '#2b2b2b']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                  style={styles.usernamePill}
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    style={styles.usernamePillText}
                  >
                    {userData.Username
                      ? ` ${userData.Username}`
                      : 'Welcome Back'}
                  </Text>
                </LinearGradient>

                {/* Home → MyPanel */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyPanel')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="home-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Home</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Offers → MyOffers */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('MyOffers')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="pricetags-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Offers</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* My Calendar → MyCalendar (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyCalendar');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="calendar-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>My Calendar</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Browse Creators → BrowseCreators */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => navigation.navigate('BrowseCreators')}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="people-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Browse Creators</Text>
                    </View>
                  </View>
                </TouchableOpacity>

                {/* Activity → MyActivityFeed (LOCKED if NOT authed) */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() => {
                    if (authChecked && !isAuthed) {
                      openInSameTab('https://collabbayadmin.com/subscribe');
                    } else {
                      navigation.navigate('MyActivityFeed');
                    }
                  }}
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="chatbubbles-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Activity</Text>
                    </View>
                    {authChecked && !isAuthed && (
                      <Ionicons
                        name="lock-closed-outline"
                        size={16}
                        color="#FDF05D"
                        style={styles.lockIcon}
                      />
                    )}
                  </View>
                </TouchableOpacity>

                {/* Account → BusinessAccountPage */}
                <TouchableOpacity
                  style={styles.navItem}
                  onPress={() =>
                    navigation.navigate('BusinessAccountPage')
                  }
                >
                  <View style={styles.navItemRow}>
                    <View style={styles.navItemMainLabel}>
                      <Ionicons
                        name="person-circle-outline"
                        size={20}
                        color="#f5f5f5"
                        style={styles.navIcon}
                      />
                      <Text style={styles.navText}>Account</Text>
                    </View>
                  </View>
                </TouchableOpacity>
              </View>

              {/* MIDDLE SECTION: Peer Map + Subscribe CTA */}
              <View style={styles.sideNavSection}>
                {/* Peer Map */}
                <TouchableOpacity
                  style={styles.peerMapCard}
                  activeOpacity={0.9}
                  onPress={() => openInSameTab('/venues')}
                >
                  <LinearGradient
                    colors={['rgba(255,255,255,0.08)', 'rgba(0,0,0,0.4)']}
                    style={styles.peerMapInner}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <View style={styles.peerMapContent}>
                      <Ionicons
                        name="map-outline"
                        size={18}
                        color="#FDF05D"
                        style={{ marginRight: 8 }}
                      />
                      <View style={{ flex: 1 }}>
                        <Text style={styles.peerMapTitle}>Peer Map</Text>
                        <Text style={styles.peerMapSubtitle}>
                          View 100+ venues on Collabbay
                        </Text>
                      </View>
                    </View>
                  </LinearGradient>
                </TouchableOpacity>

                {/* Subscribe CTA — only if NOT subscriber */}
                {!isSubscriber && (
                  <TouchableOpacity
                    style={styles.subscribeCard}
                    activeOpacity={0.9}
                    onPress={() =>
                      openInSameTab('https://collabbayadmin.com/subscribe')
                    }
                  >
                    <LinearGradient
                      colors={['#FDF05D', '#fbbf24']}
                      style={styles.subscribeGradient}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 1 }}
                    >
                      <View style={styles.subscribeContent}>
                        <Ionicons
                          name="rocket-outline"
                          size={20}
                          color="#000"
                          style={{ marginRight: 8 }}
                        />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.subscribeTitle}>
                            Subscribe & unlock
                          </Text>
                          <Text style={styles.subscribeSubtitle}>
                            Unlock Activity & Calendar, plus more.
                          </Text>
                        </View>
                      </View>
                    </LinearGradient>
                  </TouchableOpacity>
                )}
              </View>

              {/* BOTTOM: FAQ + Help */}
              <View style={styles.sideNavBottom}>
                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    openInSameTab('https://collabbayadmin.com/FAQs')
                  }
                >
                  <Ionicons
                    name="help-circle-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>FAQs</Text>
                    <Text style={styles.bottomLinkCaption}>
                      Common questions answered
                    </Text>
                  </View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={styles.bottomLink}
                  onPress={() =>
                    Linking.openURL('mailto:info@collab-bay.com')
                  }
                >
                  <Ionicons
                    name="headset-outline"
                    size={18}
                    color="#f5f5f5"
                    style={{ marginRight: 8 }}
                  />
                  <View>
                    <Text style={styles.bottomLinkText}>Help</Text>
                    <Text style={styles.bottomLinkCaption}>
                      We&apos;re here 24/7
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </LinearGradient>
          </View>

          {/* ✅ MAIN AREA (EXISTING CONTENT) */}
          <View style={styles.mainArea}>
            <View style={styles.mainInner}>
              {/* Top bar: back + ID + actions */}
              <View style={styles.topHeaderRow}>
                <View style={styles.leftHeaderGroup}>
                  <TouchableOpacity
                    onPress={() => navigation.goBack()}
                    style={styles.backButton}
                  >
                    <Ionicons name="arrow-back" size={20} color="#111827" />
                  </TouchableOpacity>
                  <View>
                    <Text style={[styles.pageTitle, { fontSize: titleBaseSize }]}>
                      Offer requests
                    </Text>
                    <Text style={styles.pageSubtitle}>Offer ID · {ListingID}</Text>
                  </View>
                </View>

                <View style={styles.rightHeaderGroup}>
                  {/* Download content button */}
                  <TouchableOpacity
                    style={styles.downloadButton}
                    onPress={handleDownloadContent}
                  >
                    <Ionicons name="image-outline" size={16} color="#111827" />
                    <Text style={styles.downloadButtonText}>Download content</Text>
                  </TouchableOpacity>

                  {/* View campaign metrics pill */}
                  <TouchableOpacity
                    style={styles.metricsButton}
                    onPress={() => {
                      if (!isAuthenticated) {
                        Linking.openURL('https://collabbayadmin.com/subscribe');
                      } else {
                        navigation.navigate('CampaignMetrics', { ListingID });
                      }
                    }}
                  >
                    <Ionicons name="analytics-outline" size={16} color="#111827" />
                    <Text style={styles.metricsButtonText}>Campaign metrics</Text>
                    {!isAuthenticated && (
                      <View style={styles.lockBadge}>
                        <Ionicons name="lock-closed-outline" size={10} color="#111827" />
                      </View>
                    )}
                  </TouchableOpacity>
                </View>
              </View>

              {/* Status + Pause/Activate section */}
              <View style={styles.offerStatusRow}>
                <View style={styles.offerStatusTextBlock}>
                  <Text style={styles.offerStatusLabel}>Offer status</Text>
                  {getStatusWithDot(toggleStatus)}
                </View>

                <TouchableOpacity
                  activeOpacity={1}
                  style={styles.pauseActivateButton}
                  onPressIn={() => setIsPauseBtnPressed(true)}
                  onPressOut={() => setIsPauseBtnPressed(false)}
                  onPress={() => setIsModalVisible(true)}
                >
                  <Ionicons name="power" size={16} color="#111827" />
                  <Text
                    style={[
                      styles.pauseActivateButtonText,
                      isPauseBtnPressed && { color: '#f59e0b' },
                    ]}
                  >
                    Pause or activate this offer
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Top metrics row + QR */}
              <View
                style={[
                  styles.metricsAndQrRow,
                  isCompact && { flexDirection: 'column' },
                ]}
              >
                <View style={styles.metricsRow}>
                  <View style={styles.metricCard}>
                    <Text style={styles.metricLabel}>Total requests</Text>
                    <Text style={styles.metricValue}>{totalRequests}</Text>
                  </View>
                  <View style={styles.metricCard}>
                    <Text style={styles.metricLabel}>Acceptance rate</Text>
                    <Text style={styles.metricValue}>{acceptanceRate}</Text>
                    {totalRequests > 0 && (
                      <Text style={styles.metricSubText}>
                        {acceptedCount} accepted / {totalRequests} total
                      </Text>
                    )}
                  </View>
                </View>

                {/* QR card */}
                <View style={styles.qrCard}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={styles.qrBadge}>
                      <Ionicons name="qr-code-outline" size={14} color="#111827" />
                    </View>
                    <Text style={styles.qrTitle}>Scan to preview offer</Text>
                  </View>
                  <Text style={styles.qrSubtitle}>
                    Quickly open this offer page on another device to check how it looks to creators.
                  </Text>


<View style={styles.qrCodeWrapper}>
  <QRCode
    value={`https://collabbay.com/collaboffer/${ListingID}`}
    size={90}
  />

  <TouchableOpacity
    style={styles.openPreviewSubtleBtn}
    onPress={openOfferPreview}
  >
    <Ionicons name="open-outline" size={15} color="#111827" />
    <Text style={styles.openPreviewSubtleBtnText}>Open preview</Text>
  </TouchableOpacity>
</View>


                </View>
              </View>

              {/* Combined followers recap */}
              <View style={styles.recapCard}>
                <Ionicons
                  name="people-outline"
                  size={20}
                  color="#111827"
                  style={{ marginRight: 8 }}
                />
                <Text style={styles.recapCardText}>
                  {cfLoading
                    ? 'Calculating reach…'
                    : `${combinedFollowers?.human ?? '—'} combined followers from collab requests`}
                </Text>
              </View>

              {/* MAIN CONTENT ROW: left = filters + list, right = calendar + upcoming */}
              <View
                style={[
                  styles.contentRow,
                  isCompact && styles.contentRowCompact,
                ]}
              >
                {/* PRIMARY COLUMN – filters + main feed */}
                <View
                  style={[
                    styles.primaryColumn,
                    isCompact && styles.primaryColumnCompact,
                  ]}
                >
                  {/* Filters & search */}
                  <View style={styles.filtersCard}>
                    {/* Filters header */}
                    <View style={styles.filtersHeaderRow}>
                      <View style={styles.filtersTitleRow}>
                        <Ionicons
                          name="funnel-outline"
                          size={16}
                          color="#111827"
                          style={{ marginRight: 6 }}
                        />
                        <Text style={styles.filtersTitle}>Filter & sort requests</Text>
                      </View>
                      <Text style={styles.filtersSubtitle}>
                        Refine the list by status or quickly focus on visits that are coming up.
                      </Text>
                    </View>

                    {/* Search */}
                    <View style={styles.searchContainer}>
                      <Ionicons
                        name="search-outline"
                        size={18}
                        color="#9ca3af"
                        style={styles.searchIcon}
                      />
                      <TextInput
                        style={styles.searchInput}
                        placeholder="Search by creator name or collab headline"
                        placeholderTextColor="#9ca3af"
                        value={searchText}
                        onChangeText={setSearchText}
                      />
                    </View>

                    {/* Chips + quick filter + sort */}
                    <View
                      style={[
                        styles.controlsRow,
                        isCompact && { flexDirection: 'column', alignItems: 'flex-start' },
                      ]}
                    >
                      <View style={isCompact ? styles.chipsWrapCompact : styles.chipsWrap}>
                        {['Pending', 'Upcoming', 'Redeemed', 'Expired', 'Cancelled'].map((s) => {
                          const active = activeStatuses.has(s);
                          return (
                            <TouchableOpacity
                              key={s}
                              onPress={() => toggleStatusChip(s)}
                              style={[styles.chip, active && styles.chipActive]}
                            >
                              <Text style={[styles.chipText, active && styles.chipTextActive]}>
                                {s}
                              </Text>
                            </TouchableOpacity>
                          );
                        })}
                        {activeStatuses.size > 0 && (
                          <TouchableOpacity onPress={clearStatusChips} style={styles.clearChip}>
                            <Ionicons name="close-circle" size={14} color="#111827" />
                            <Text style={styles.clearChipText}>Clear filters</Text>
                          </TouchableOpacity>
                        )}
                      </View>

                      <View style={styles.rightControls}>
                        <TouchableOpacity
                          onPress={handleQuickFilterPress}
                          style={[
                            styles.quickFilterButton,
                            quickFilterActive && styles.quickFilterButtonActive,
                          ]}
                        >
                          <Ionicons
                            name="flash-outline"
                            size={14}
                            color={quickFilterActive ? '#111827' : '#4b5563'}
                            style={{ marginRight: 4 }}
                          />
                          <Text
                            style={[
                              styles.quickFilterText,
                              quickFilterActive && styles.quickFilterTextActive,
                            ]}
                          >
                            Pending & upcoming only
                          </Text>
                        </TouchableOpacity>

                        <View style={styles.sortControl}>
                          <Ionicons name="swap-vertical-outline" size={14} color="#111827" />
                          <Text style={styles.sortLabel}>Sort</Text>
                          <TouchableOpacity
                            onPress={() => {
                              const opts = ['Newest', 'Oldest', 'A→Z', 'Z→A', 'Status'];
                              const i = opts.indexOf(sortBy);
                              setSortBy(opts[(i + 1) % opts.length]);
                            }}
                            style={styles.sortButton}
                          >
                            <Text style={styles.sortButtonText}>{sortBy}</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  </View>

                  {/* Collab list */}
                  <View style={styles.listWrapper}>
                    {displayedCollabs.length === 0 ? (
                      <View style={styles.emptyState}>
                        <Ionicons name="mail-open-outline" size={28} color="#9ca3af" />
                        <Text style={styles.emptyTitle}>No collab requests match these filters</Text>
                        <Text style={styles.emptySubtitle}>
                          Clear filters or check back once creators have requested this offer.
                        </Text>
                      </View>
                    ) : (
                      <FlatList
                        data={displayedCollabs}
                        renderItem={renderCollabItem}
                        keyExtractor={(item, index) => index.toString()}
                        contentContainerStyle={{ paddingBottom: 24 }}
                      />
                    )}
                  </View>
                </View>

                {/* SECONDARY COLUMN – calendar + upcoming soon */}
                <View
                  style={[
                    styles.secondaryColumn,
                    isCompact && styles.secondaryColumnCompact,
                  ]}
                >
                  {/* Calendar summary section */}
                  <View style={styles.calendarCard}>
                    <View style={styles.calendarHeaderRow}>
                      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={styles.calendarTitle}>Calendar overview</Text>

                        <View style={styles.calendarLegendRow}>
                          <View style={styles.calendarLegendItem}>
                            <View
                              style={[styles.calendarLegendDot, { backgroundColor: '#facc15' }]}
                            />
                            <Text style={styles.calendarLegendLabel}>Pending</Text>
                          </View>
                          <View style={styles.calendarLegendItem}>
                            <View
                              style={[styles.calendarLegendDot, { backgroundColor: '#22c55e' }]}
                            />
                            <Text style={styles.calendarLegendLabel}>Upcoming</Text>
                          </View>
                        </View>
                      </View>

                      <Text style={styles.calendarSubtitle}>
                        See which days have pending or upcoming visits at a glance.
                      </Text>
                    </View>

                    <Calendar
                      style={styles.calendar}
                      hideExtraDays
                      firstDay={1}
                      markingType="multi-dot"
                      markedDates={calendarMarks}
                      onDayPress={(day) => {
                        setSelectedCalendarDate(day.dateString);
                      }}
                      theme={{
                        backgroundColor: 'transparent',
                        calendarBackground: 'transparent',
                        textSectionTitleColor: '#6b7280',
                        dayTextColor: '#111827',
                        todayTextColor: '#1d4ed8',
                        textDisabledColor: '#d1d5db',
                        monthTextColor: '#111827',
                        arrowColor: '#111827',
                        textDayFontSize: 14,
                        textDayFontWeight: '700',
                        dotStyle: {
                          width: 8,
                          height: 8,
                          borderRadius: 4,
                          marginTop: 0,
                        },
                        textMonthFontSize: 15,
                        textDayHeaderFontSize: 11,
                        selectedDayBackgroundColor: '#111827',
                        selectedDayTextColor: '#fdf05d',
                      }}
                    />

                    {selectedCalendarDate && selectedDateCollabs.length > 0 && (
                      <View style={styles.calendarSummaryCard}>
                        <Text style={styles.calendarSummaryTitle}>
                          Visits on {formatSummaryDate(selectedCalendarDate)}
                        </Text>
                        {selectedDateCollabs.map((c) => {
                          const status = getCollabStatus(c);
                          return (
                            <TouchableOpacity
                              key={c.CollabID}
                              style={styles.calendarSummaryItem}
                              onPress={() => {
                                if (!isAuthenticated) {
                                  Linking.openURL('https://collabbayadmin.com/subscribe');
                                } else {
                                  navigation.navigate('BookingRequest', {
                                    CollabID: c.CollabID,
                                  });
                                }
                              }}
                            >
                              <View style={{ flex: 1 }}>
                                <Text style={styles.calendarSummaryName}>{c.Promoter}</Text>
                                <Text style={styles.calendarSummaryMeta}>
                                  {formatVisitDate(c.CreditAvailableStart)} · {status}
                                </Text>
                              </View>
                              <Ionicons name="chevron-forward" size={18} color="#9ca3af" />
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    )}

                    {selectedCalendarDate && selectedDateCollabs.length === 0 && (
                      <Text style={styles.calendarEmptyText}>
                        No pending or upcoming visits on this day.
                      </Text>
                    )}
                  </View>

                  {/* Upcoming soon (next 3 days) */}
                  <View style={styles.upcomingCard}>
                    <View style={styles.upcomingHeaderRow}>
                      <View>
                        <Text style={styles.upcomingTitle}>Coming up soon</Text>
                        <Text style={styles.upcomingSubtitle}>
                          Visits due in the next 3 days.
                        </Text>
                      </View>
                      <Ionicons name="time-outline" size={18} color="#6b7280" />
                    </View>

                    {upcomingSoonCollabs.length === 0 ? (
                      <Text style={styles.upcomingEmptyText}>
                        No visits scheduled in the next 3 days.
                      </Text>
                    ) : (
                      upcomingSoonCollabs.map((c) => {
                        const status = getCollabStatus(c);
                        return (
                          <TouchableOpacity
                            key={c.CollabID}
                            style={styles.upcomingItem}
                            onPress={() => {
                              if (!isAuthenticated) {
                                Linking.openURL('https://collabbayadmin.com/subscribe');
                              } else {
                                navigation.navigate('BookingRequest', { CollabID: c.CollabID });
                              }
                            }}
                          >
                            <View style={{ flex: 1 }}>
                              <Text style={styles.upcomingName}>
                                {!isAuthenticated ? (
                                  <>
                                    {c.Promoter.charAt(0)}
                                    <Text
                                      style={{
                                        color: 'transparent',
                                        textShadowColor: 'rgba(0, 0, 0, 0.7)',
                                        textShadowOffset: { width: 0, height: 0 },
                                        textShadowRadius: 10,
                                        userSelect: 'none',
                                        letterSpacing: 2,
                                      }}
                                      selectable={false}
                                    >
                                      {c.Promoter.slice(1)}
                                    </Text>
                                  </>
                                ) : (
                                  c.Promoter
                                )}
                              </Text>
                              <Text style={styles.upcomingMeta}>
                                {formatVisitDate(c.CreditAvailableStart)} · {status}
                              </Text>
                            </View>
                            <Ionicons name="chevron-forward" size={18} color="#9ca3af" />
                          </TouchableOpacity>
                        );
                      })
                    )}
                  </View>
                </View>
              </View>

              {/* Pause / Activate modal */}
              <Modal
                animationType="slide"
                transparent={true}
                visible={isModalVisible}
                onRequestClose={() => {
                  setIsModalVisible(!isModalVisible);
                }}
              >
                <View style={styles.modalContainer}>
                  <View style={styles.modalContent}>
                    <TouchableOpacity
                      style={styles.closeButton}
                      onPress={() => setIsModalVisible(false)}
                    >
                      <Ionicons name="close" size={24} color="#111827" />
                    </TouchableOpacity>

                    <Text style={styles.modalTitle}>Change offer status</Text>
                    <Text style={styles.disclaimer}>
                      Pausing will hide this offer from the marketplace. Any collabs already agreed will
                      still be active unless cancelled.
                    </Text>

                    <View style={styles.buttonRow}>
                      <TouchableOpacity
                        style={[
                          styles.toggleButton,
                          newToggleStatus === 'Live' && styles.selectedButton,
                        ]}
                        onPress={() => setNewToggleStatus('Live')}
                      >
                        <Text
                          style={[
                            styles.toggleButtonText,
                            newToggleStatus === 'Live' && styles.selectedText,
                          ]}
                        >
                          Live
                        </Text>
                      </TouchableOpacity>

                      <TouchableOpacity
                        style={[
                          styles.toggleButton,
                          newToggleStatus === 'Pause' && styles.selectedButton,
                        ]}
                        onPress={() => setNewToggleStatus('Pause')}
                      >
                        <Text
                          style={[
                            styles.toggleButtonText,
                            newToggleStatus === 'Pause' && styles.selectedText,
                          ]}
                        >
                          Pause
                        </Text>
                      </TouchableOpacity>
                    </View>

                    <TouchableOpacity style={styles.confirmButton} onPress={handleSubmitStatusChange}>
                      <Text style={styles.confirmButtonText}>Confirm</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </Modal>
            </View>
          </View>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f3f3f3',
  },
  // SHELL
  appShell: {
    flex: 1,
    backgroundColor: '#f3f4f6',
  },
  shellRow: {
    flex: 1,
    flexDirection: 'row',
  },

  // SIDE NAV (copied style)
  sideNav: {
    width: 250,
    backgroundColor: '#000',
  },
  sideNavInner: {
    flex: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    overflow: 'hidden',
    position: 'relative',
  },
  sideNavGlow: {
    position: 'absolute',
    top: 140,
    left: -60,
    width: 260,
    height: 260,
    borderRadius: 130,
    opacity: 0.55,
  },
  sideNavHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 26,
    zIndex: 2,
  },
  sideNavLogoImage: {
    width: 38,
    height: 38,
    marginRight: 10,
  },
  logoWordmark: {
    fontSize: 16,
    color: '#f5f5f5',
    fontWeight: '600',
    letterSpacing: 0.3,
  },
  logoDot: {
    color: '#FDF05D',
    fontSize: 18,
    fontWeight: '800',
  },
  logoDesktopTag: {
    fontSize: 9,
    color: '#b3b3b3',
    letterSpacing: 2,
    marginTop: 2,
  },
  sideNavSection: {
    marginTop: 12,
    marginBottom: 20,
    zIndex: 2,
  },
  usernamePill: {
    paddingTop: 4,
    paddingBottom: 8,
    paddingHorizontal: 10,
    borderRadius: 999,
    alignSelf: 'flex-start',
    maxWidth: '100%',
    marginLeft: 1,
    marginBottom: 10,
  },
  usernamePillText: {
    fontSize: 8,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    textAlign: 'left',
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 9,
    paddingHorizontal: 8,
    borderRadius: 10,
    marginBottom: 6,
  },
  navIcon: {
    marginRight: 10,
  },
  navItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  navItemMainLabel: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  navText: {
    color: '#f5f5f5',
    fontSize: 14,
    fontWeight: '500',
  },
  lockIcon: {
    marginLeft: 6,
  },

  // Peer Map card
  peerMapCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 4,
    marginBottom: 10,
  },
  peerMapInner: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  peerMapContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  peerMapTitle: {
    color: '#f5f5f5',
    fontSize: 13,
    fontWeight: '600',
  },
  peerMapSubtitle: {
    color: '#b3b3b3',
    fontSize: 11,
    marginTop: 2,
  },

  // Subscribe CTA
  subscribeCard: {
    borderRadius: 14,
    overflow: 'hidden',
    marginTop: 10,
  },
  subscribeGradient: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  subscribeContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  subscribeTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#000',
  },
  subscribeSubtitle: {
    fontSize: 11,
    color: '#1f2933',
    marginTop: 2,
  },

  // BOTTOM LINKS
  sideNavBottom: {
    marginTop: 'auto',
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#181818',
    zIndex: 2,
  },
  bottomLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
  },
  bottomLinkText: {
    fontSize: 13,
    color: '#f5f5f5',
    fontWeight: '600',
  },
  bottomLinkCaption: {
    fontSize: 11,
    color: '#b3b3b3',
    marginTop: 2,
  },

  /* MAIN AREA */
  mainArea: {
    flex: 1,
    paddingHorizontal: 18,
    paddingVertical: 14,
    backgroundColor: '#e5e7eb',
  },
  mainInner: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 20,
    maxWidth: 1120,
    alignSelf: 'center',
  },

  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* OLD SIDEBAR STYLES (unused but kept) */
  sidebar: {
    width: 110,
    backgroundColor: '#050816',
    paddingVertical: 16,
    paddingHorizontal: 10,
    borderRightWidth: 1,
    borderRightColor: '#111827',
    justifyContent: 'flex-start',
  },
  sidebarLogoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    paddingHorizontal: 4,
  },
  sidebarLogoMark: {
    width: 26,
    height: 26,
    borderRadius: 999,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  sidebarLogoMarkText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  sidebarLogoText: {
    flexShrink: 1,
    color: '#f9fafb',
    fontSize: 13,
    fontWeight: '600',
  },
  sidebarNavSection: {
    marginTop: 4,
  },
  sidebarNavItem: {
    flexDirection: 'column',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    borderRadius: 18,
    marginBottom: 6,
  },
  sidebarNavIcon: {
    marginBottom: 3,
  },
  sidebarNavLabel: {
    fontSize: 10,
    color: '#e5e7eb',
    fontWeight: '500',
  },
  sidebarSpacer: {
    flex: 1,
  },
  sidebarPrimaryCta: {
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
  },
  sidebarPrimaryCtaText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#111827',
  },

  // HEADER
  topHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  leftHeaderGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  backButton: {
    width: 34,
    height: 34,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  pageTitle: {
    fontWeight: '700',
    color: '#111827',
  },
  pageSubtitle: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 3,
  },
  rightHeaderGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  viewListingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#ead43b',
  },
  viewListingButtonText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  downloadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e5e7eb',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  downloadButtonText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  metricsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f3f4f6',
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    position: 'relative',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  metricsButtonText: {
    marginLeft: 6,
    fontSize: 12,
    fontWeight: '600',
    color: '#111827',
  },
  lockBadge: {
    marginLeft: 6,
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    padding: 2,
  },

  // STATUS + PAUSE
  offerStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  offerStatusTextBlock: {
    flexDirection: 'column',
  },
  offerStatusLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  pauseActivateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e5e7eb',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
  },
  pauseActivateButtonText: {
    color: '#111827',
    fontSize: 12,
    marginLeft: 8,
    fontWeight: '600',
  },

  // METRIC CARDS + QR
  metricsAndQrRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metricsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    flex: 1,
    marginRight: 10,
  },
  metricCard: {
    flexGrow: 1,
    minWidth: 160,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    elevation: 1,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  metricLabel: {
    fontSize: 11,
    color: '#6b7280',
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  metricSubText: {
    marginTop: 2,
    fontSize: 11,
    color: '#6b7280',
  },

  qrCard: {
    width: 220,
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.04,
    shadowOffset: { width: 0, height: 1 },
    shadowRadius: 3,
    elevation: 1,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  qrBadge: {
    width: 22,
    height: 22,
    borderRadius: 999,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 6,
  },
  qrTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  qrSubtitle: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 4,
  },
  qrCodeWrapper: {
    marginTop: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // COMBINED FOLLOWERS RECAP
  recapCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdf05d',
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 16,
    marginBottom: 14,
    shadowColor: '#000',
    shadowOpacity: 0.06,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    borderWidth: 1,
    borderColor: '#ead43b',
  },
  recapCardText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
    flexShrink: 1,
  },

  // MAIN CONTENT ROW
  contentRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 14,
  },
  contentRowCompact: {
    flexDirection: 'column',
  },

  primaryColumn: {
    flex: 1.4,
    minWidth: 0,
  },
  primaryColumnCompact: {
    width: '100%',
  },
  secondaryColumn: {
    flex: 0.9,
    minWidth: 260,
  },
  secondaryColumnCompact: {
    width: '100%',
    marginTop: 10,
  },

  // CALENDAR CARD
  calendarCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  calendarHeaderRow: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  calendarTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  calendarSubtitle: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },
  calendarLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 10,
  },
  calendarLegendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
  },
  calendarLegendDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    marginRight: 4,
  },
  calendarLegendLabel: {
    fontSize: 11,
    color: '#4b5563',
  },
  calendar: {
    marginTop: 6,
    borderRadius: 12,
  },
  calendarSummaryCard: {
    marginTop: 10,
    borderRadius: 12,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  calendarSummaryTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  calendarSummaryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  calendarSummaryName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  calendarSummaryMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 1,
  },
  calendarEmptyText: {
    marginTop: 8,
    fontSize: 11,
    color: '#6b7280',
  },

  // UPCOMING CARD
  upcomingCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  upcomingHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  upcomingTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  upcomingSubtitle: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },
  upcomingEmptyText: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 4,
  },
  upcomingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  upcomingName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#111827',
  },
  upcomingMeta: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 1,
  },

  // FILTERS CARD
  filtersCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
    elevation: 1,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  filtersHeaderRow: {
    marginBottom: 6,
  },
  filtersTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  filtersTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#111827',
  },
  filtersSubtitle: {
    fontSize: 11,
    color: '#6b7280',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginTop: 8,
    marginBottom: 8,
  },
  searchIcon: {
    marginRight: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
  },

  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  chipsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    flex: 1,
    paddingRight: 6,
  },
  chipsWrapCompact: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    width: '100%',
    marginTop: 4,
  },
  chip: {
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  chipActive: {
    backgroundColor: '#eef2ff',
    borderColor: '#c7d2fe',
  },
  chipText: {
    fontSize: 11,
    color: '#111827',
    fontWeight: '600',
  },
  chipTextActive: {
    color: '#4338ca',
  },
  clearChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  clearChipText: {
    fontSize: 11,
    color: '#111827',
    fontWeight: '600',
    marginLeft: 4,
  },

  rightControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  quickFilterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  quickFilterButtonActive: {
    backgroundColor: '#fdf05d',
    borderColor: '#eab308',
  },
  quickFilterText: {
    fontSize: 11,
    color: '#4b5563',
    fontWeight: '600',
  },
  quickFilterTextActive: {
    color: '#111827',
  },

  sortControl: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sortLabel: {
    fontSize: 11,
    color: '#4b5563',
    marginHorizontal: 4,
  },
  sortButton: {
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  sortButtonText: {
    fontSize: 11,
    color: '#111827',
    fontWeight: '700',
  },

  // LIST AREA
  listWrapper: {
    flex: 1,
    marginTop: 4,
  },
  collabsCard: {
    backgroundColor: '#ffffff',
    borderRadius: 18,
    borderColor: '#e5e7eb',
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 3,
    elevation: 1,
    position: 'relative',
  },
  collabCardHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  collabHeaderLeft: {
    flexDirection: 'row',
    flexShrink: 1,
    alignItems: 'center',
  },
  collabHeaderRight: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  cardChevronWrapper: {
    marginTop: 6,
  },
  collabCreatorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
  },
  creatorAvatar: {
    width: 44,
    height: 44,
    borderRadius: 999,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    overflow: 'hidden',
  },
  creatorAvatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 999,
  },
  creatorAvatarText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  collabsCardIDTextPromoter: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
  },
  collabDateText: {
    fontSize: 11,
    color: '#6b7280',
    marginTop: 2,
  },

  collabMetaRow: {
    flexDirection: 'row',
    marginTop: 6,
  },
  metaItem: {
    marginRight: 16,
  },
  metaItemPrimary: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: '#e0f2fe',
  },
  metaIconRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaLabelPrimary: {
    fontSize: 11,
    color: '#1d4ed8',
    fontWeight: '600',
  },
  metaValuePrimary: {
    fontSize: 12,
    color: '#1f2937',
    marginTop: 1,
    fontWeight: '700',
  },
  metaDaysText: {
    marginTop: 2,
    fontSize: 11,
    color: '#4b5563',
  },

  // STATUS BADGE
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
  },
  statusText: {
    marginLeft: 6,
    fontSize: 11,
    fontWeight: '600',
  },
  expiredStatus: {
    backgroundColor: '#fee2e2',
  },
  redeemedStatus: {
    backgroundColor: '#dcfce7',
  },
  upcomingStatus: {
    backgroundColor: '#e0f2fe',
  },
  pendingStatus: {
    backgroundColor: '#fef9c3',
  },
  cancelledStatus: {
    backgroundColor: '#e5e7eb',
  },

  // MODAL
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    padding: 20,
    borderRadius: 16,
    width: '80%',
    maxWidth: 420,
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 10,
    color: '#111827',
  },
  disclaimer: {
    fontSize: 12,
    color: '#4b5563',
    textAlign: 'center',
    marginBottom: 18,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  toggleButton: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 10,
    borderRadius: 999,
    alignItems: 'center',
    marginHorizontal: 5,
  },
  selectedButton: {
    backgroundColor: '#111827',
  },
  toggleButtonText: {
    color: '#111827',
    fontWeight: '600',
  },
  confirmButton: {
    marginTop: 18,
    backgroundColor: '#111827',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 999,
  },
  confirmButtonText: {
    color: 'white',
    fontWeight: '700',
  },
  selectedText: {
    color: '#fdf05d',
  },

  // EMPTY STATE
  emptyState: {
    paddingVertical: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 10,
    fontSize: 15,
    fontWeight: '600',
    color: '#111827',
  },
  emptySubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: '#6b7280',
    textAlign: 'center',
    paddingHorizontal: 24,
  },
  openPreviewSubtleBtn: {
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: '#f3f3f3',
  paddingVertical: 6,
  paddingHorizontal: 10,
  borderRadius: 999,
  borderWidth: 1,
  borderColor: '#e5e7eb',
  marginTop: 10,
  alignSelf: 'center',
},
openPreviewSubtleBtnText: {
  marginLeft: 6,
  fontSize: 11,
  fontWeight: '600',
  color: '#111827',
},

});

export default OfferAssociatedCollabs;
